<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  APPROVAL_MODE,
  APPROVAL_NODE_TYPE,
  DOCUMENT_TYPE,
  ROLE_CODE,
  type ApprovalMode,
  type ApprovalNodeType,
  type DocumentType,
  type RoleCode,
} from '@hsl-ems/contracts';

import { p1Api, type OrganizationListItem, type UserListItem } from '@/lib/p1-api';
import { p2Api, type ExpenseCategory } from '@/lib/p2-api';
import { p3Api, type ApprovalFlowSummary, type ApprovalFlowVersionInput } from '@/lib/p3-api';

const router = useRouter();
const flows = ref<ApprovalFlowSummary[]>([]);
const organizations = ref<OrganizationListItem[]>([]);
const users = ref<UserListItem[]>([]);
const categories = ref<ExpenseCategory[]>([]);
const selectedFlowId = ref('');
const loading = ref(true);
const saving = ref(false);
const canManage = ref(false);
const error = ref('');
const notice = ref('');
const validation = ref<Record<string, { valid: boolean; errors: string[] }>>({});
const simulation = ref<Awaited<ReturnType<typeof p3Api.simulateFlow>>>();

const newFlow = reactive({ code: '', name: '', description: '' });
const version = reactive(newVersionForm());
const simulator = reactive({
  operatingOrgId: '',
  documentType: DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT as DocumentType,
  expenseCategoryId: '',
  amount: '0.00',
  at: '',
});

const selectedFlow = computed(() => flows.value.find((flow) => flow.id === selectedFlowId.value));
const operatingOrganizations = computed(() =>
  organizations.value.filter((item) => item.type !== 'DEPARTMENT'),
);
const documentTypes = [
  { value: DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT, label: '费用报销' },
  { value: DOCUMENT_TYPE.LOAN, label: '借款申请' },
  { value: DOCUMENT_TYPE.CASH_REPAYMENT, label: '现金还款' },
  { value: DOCUMENT_TYPE.CORPORATE_PAYMENT, label: '对公付款' },
];
const nodeTypes: Array<{ value: ApprovalNodeType; label: string }> = [
  { value: APPROVAL_NODE_TYPE.APPLICANT_MANAGER, label: '申请人属地负责人' },
  { value: APPROVAL_NODE_TYPE.ORG_ROLE, label: '组织角色' },
  { value: APPROVAL_NODE_TYPE.FIXED_USER, label: '固定人员' },
];
const roleOptions: Array<{ value: RoleCode; label: string }> = [
  { value: ROLE_CODE.LOCAL_MANAGER, label: '属地负责人' },
  { value: ROLE_CODE.ORG_ADMIN, label: '组织管理员' },
  { value: ROLE_CODE.HQ_FINANCE, label: '总部财务' },
  { value: ROLE_CODE.HQ_LEADER, label: '总部领导' },
  { value: ROLE_CODE.HQ_CASHIER, label: '总部出纳' },
];

onMounted(load);

function localDate(hours = 1) {
  const date = new Date(Date.now() + hours * 60 * 60_000);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function newVersionForm() {
  return {
    effectiveFrom: localDate(),
    effectiveUntil: '',
    priority: 0,
    conditions: [
      {
        organizationId: '',
        includeDescendants: false,
        documentType: DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT as DocumentType,
        expenseCategoryId: '',
        minAmount: '0.00',
        maxAmount: '',
      },
    ],
    nodes: [
      {
        name: '属地负责人审批',
        nodeType: APPROVAL_NODE_TYPE.APPLICANT_MANAGER as ApprovalNodeType,
        approvalMode: APPROVAL_MODE.ANY_ONE as ApprovalMode,
        candidateRoleCode: '' as RoleCode | '',
        scopeOrganizationId: '',
        fixedUserId: '',
        expectedDurationHours: 24,
      },
    ],
  };
}

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const me = await p1Api.me();
    if (me.mustChangePassword) return void (await router.replace('/change-password'));
    if (!me.permissions.includes('master-data:read')) {
      error.value = '当前账号无权读取审批流程';
      return;
    }
    canManage.value =
      me.permissions.includes('master-data:manage') && me.roleCodes.includes('GROUP_ADMIN');
    const requests: [
      Promise<ApprovalFlowSummary[]>,
      Promise<OrganizationListItem[]>,
      Promise<ExpenseCategory[]>,
      Promise<UserListItem[] | null>,
    ] = [
      p3Api.flows(),
      p1Api.organizations(),
      p2Api.categories(),
      me.permissions.includes('user:read') ? p1Api.users() : Promise.resolve(null),
    ];
    const [flowItems, orgItems, categoryItems, userItems] = await Promise.all(requests);
    flows.value = flowItems;
    organizations.value = orgItems;
    categories.value = categoryItems;
    users.value = userItems ?? [];
    if (!selectedFlowId.value && flows.value.length) selectedFlowId.value = flows.value[0]!.id;
    const defaultOrg = operatingOrganizations.value[0]?.id ?? '';
    if (!version.conditions[0]!.organizationId) version.conditions[0]!.organizationId = defaultOrg;
    if (!simulator.operatingOrgId) simulator.operatingOrgId = defaultOrg;
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : '审批流程加载失败';
    if (message.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = message;
  } finally {
    loading.value = false;
  }
}

async function createFlow() {
  if (!canManage.value || newFlow.code.trim().length < 2 || newFlow.name.trim().length < 2) {
    error.value = '流程编码和名称至少填写 2 个字符';
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const created = await p3Api.createFlow({
      code: newFlow.code.trim().toUpperCase(),
      name: newFlow.name.trim(),
      ...(newFlow.description.trim() ? { description: newFlow.description.trim() } : {}),
    });
    newFlow.code = '';
    newFlow.name = '';
    newFlow.description = '';
    selectedFlowId.value = created.id;
    await load();
    notice.value = '审批流程已创建，请继续创建草稿版本';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '流程创建失败';
  } finally {
    saving.value = false;
  }
}

function addCondition() {
  const first = version.conditions[0]!;
  version.conditions.push({ ...first, expenseCategoryId: '', minAmount: '0.00', maxAmount: '' });
}

function addNode() {
  version.nodes.push({
    name: `审批节点 ${version.nodes.length + 1}`,
    nodeType: APPROVAL_NODE_TYPE.ORG_ROLE,
    approvalMode: APPROVAL_MODE.ANY_ONE,
    candidateRoleCode: ROLE_CODE.HQ_FINANCE,
    scopeOrganizationId: '',
    fixedUserId: '',
    expectedDurationHours: 24,
  });
}

function removeCondition(index: number) {
  if (version.conditions.length > 1) version.conditions.splice(index, 1);
}

function removeNode(index: number) {
  if (version.nodes.length > 1) version.nodes.splice(index, 1);
}

async function createVersion() {
  if (!canManage.value || !selectedFlowId.value) return;
  if (version.conditions.some((item) => !item.organizationId)) {
    error.value = '每个条件必须选择组织';
    return;
  }
  if (version.nodes.some((item) => item.name.trim().length < 2)) {
    error.value = '每个审批节点必须填写至少 2 个字符的名称';
    return;
  }
  const payload: ApprovalFlowVersionInput = {
    effectiveFrom: new Date(version.effectiveFrom).toISOString(),
    ...(version.effectiveUntil
      ? { effectiveUntil: new Date(version.effectiveUntil).toISOString() }
      : {}),
    priority: Number(version.priority),
    conditions: version.conditions.map((item) => ({
      organizationId: item.organizationId,
      includeDescendants: item.includeDescendants,
      documentType: item.documentType,
      ...(item.expenseCategoryId ? { expenseCategoryId: item.expenseCategoryId } : {}),
      minAmount: item.minAmount,
      ...(item.maxAmount ? { maxAmount: item.maxAmount } : {}),
    })),
    nodes: version.nodes.map((item, index) => ({
      sequence: index + 1,
      name: item.name.trim(),
      nodeType: item.nodeType,
      approvalMode: item.approvalMode,
      ...(item.nodeType === APPROVAL_NODE_TYPE.ORG_ROLE && item.candidateRoleCode
        ? { candidateRoleCode: item.candidateRoleCode }
        : {}),
      ...(item.scopeOrganizationId ? { scopeOrganizationId: item.scopeOrganizationId } : {}),
      ...(item.nodeType === APPROVAL_NODE_TYPE.FIXED_USER && item.fixedUserId
        ? { fixedUserId: item.fixedUserId }
        : {}),
      ...(item.expectedDurationHours
        ? { expectedDurationHours: Number(item.expectedDurationHours) }
        : {}),
    })),
  };
  saving.value = true;
  error.value = '';
  try {
    await p3Api.createFlowVersion(selectedFlowId.value, payload);
    Object.assign(version, newVersionForm());
    await load();
    notice.value = '草稿版本已创建；发布前请先运行校验';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '版本创建失败';
  } finally {
    saving.value = false;
  }
}

async function validateVersion(id: string) {
  error.value = '';
  try {
    const result = await p3Api.validateFlowVersion(id);
    validation.value[id] = {
      valid: result.valid,
      errors: result.errors.map((item) => `${item.field}：${validationReason(item.reason)}`),
    };
    notice.value = result.valid ? '版本校验通过，可以发布' : '版本校验未通过，请检查正式配置';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '版本校验失败';
  }
}

async function publishVersion(id: string, revision: number) {
  if (!canManage.value || validation.value[id]?.valid !== true) {
    error.value = '必须先通过当前版本校验才能发布';
    return;
  }
  if (!window.confirm('发布后该版本将参与正式单据匹配，确定继续吗？')) return;
  saving.value = true;
  error.value = '';
  try {
    await p3Api.publishFlowVersion(id, revision);
    await load();
    notice.value = '审批流程版本已发布';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '版本发布失败';
  } finally {
    saving.value = false;
  }
}

async function simulate() {
  if (!simulator.operatingOrgId || Number(simulator.amount) < 0) {
    error.value = '请选择经营组织并填写有效金额';
    return;
  }
  error.value = '';
  simulation.value = undefined;
  try {
    simulation.value = await p3Api.simulateFlow({
      operatingOrgId: simulator.operatingOrgId,
      documentType: simulator.documentType,
      ...(simulator.expenseCategoryId ? { expenseCategoryId: simulator.expenseCategoryId } : {}),
      amount: simulator.amount,
      ...(simulator.at ? { at: new Date(simulator.at).toISOString() } : {}),
    });
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '流程模拟失败';
  }
}

function validationReason(reason: string) {
  const labels: Record<string, string> = {
    REQUIRED: '必填配置缺失',
    NO_ACTIVE_OPERATING_ORGANIZATION: '没有有效经营组织',
    NO_ACTIVE_ASSIGNEE: '存在节点没有有效处理人',
    AMBIGUOUS_OVERLAP: '与同优先级已发布规则重叠',
  };
  return labels[reason] ?? reason;
}

function dateTime(value: string | null) {
  return value
    ? new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '长期有效';
}
</script>

<template>
  <main class="workspace-shell flow-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/documents">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>审批流程配置</small></span>
      </RouterLink>
      <nav aria-label="主导航">
        <RouterLink to="/documents">我的单据</RouterLink>
        <RouterLink to="/approval-tasks">审批待办</RouterLink>
        <RouterLink class="active" to="/approval-flows">审批流程</RouterLink>
        <RouterLink to="/admin">组织与权限</RouterLink>
      </nav>
      <RouterLink class="text-link" to="/admin">返回管理</RouterLink>
    </header>

    <section class="workspace-title-row">
      <div>
        <p class="eyebrow">P3 · FR-WF-001</p>
        <h1>审批流程与模拟器</h1>
        <p>草稿必须通过正式处理人和规则重叠校验，发布后才参与单据匹配。</p>
      </div>
      <button class="quiet-button" type="button" :disabled="loading" @click="load">刷新</button>
    </section>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <p v-if="notice" class="inline-alert inline-alert--success" role="status">{{ notice }}</p>
    <section v-if="loading" class="document-empty">正在加载审批流程…</section>

    <template v-else>
      <section v-if="canManage" class="form-section compact-form-section">
        <div class="section-heading">
          <div>
            <p class="eyebrow">新建流程</p>
            <h2>流程基本信息</h2>
          </div>
          <span class="scope-chip">仅集团管理员</span>
        </div>
        <form class="flow-inline-form" @submit.prevent="createFlow">
          <label
            ><span>流程编码</span
            ><input v-model="newFlow.code" maxlength="64" placeholder="EXPENSE_STD"
          /></label>
          <label
            ><span>流程名称</span
            ><input v-model="newFlow.name" maxlength="120" placeholder="标准费用审批"
          /></label>
          <label class="wide-field"
            ><span>说明</span
            ><input
              v-model="newFlow.description"
              maxlength="500"
              placeholder="适用范围和管理说明（选填）"
          /></label>
          <button type="submit" :disabled="saving">创建流程</button>
        </form>
      </section>

      <section class="flow-layout">
        <aside class="form-section flow-list-panel">
          <div class="section-heading">
            <h2>流程清单</h2>
            <span>{{ flows.length }} 个</span>
          </div>
          <button
            v-for="flow in flows"
            :key="flow.id"
            type="button"
            class="flow-list-item"
            :class="{ active: selectedFlowId === flow.id }"
            @click="selectedFlowId = flow.id"
          >
            <strong>{{ flow.code }}</strong
            ><span>{{ flow.name }}</span
            ><small>{{ flow.versions.length }} 个版本</small>
          </button>
          <div v-if="!flows.length" class="compact-empty">尚未创建审批流程</div>
        </aside>

        <div class="flow-main-column">
          <section v-if="selectedFlow" class="form-section">
            <div class="section-heading">
              <div>
                <p class="eyebrow">{{ selectedFlow.code }}</p>
                <h2>{{ selectedFlow.name }}</h2>
                <p>{{ selectedFlow.description || '暂无说明' }}</p>
              </div>
            </div>
            <div v-if="selectedFlow.versions.length" class="version-list">
              <article v-for="item in selectedFlow.versions" :key="item.id">
                <div>
                  <strong>版本 {{ item.versionNumber }}</strong
                  ><span class="status-pill" :data-status="item.status">{{ item.status }}</span>
                </div>
                <p>
                  优先级 {{ item.priority }} · {{ dateTime(item.effectiveFrom) }} 至
                  {{ dateTime(item.effectiveUntil) }}
                </p>
                <div class="version-actions">
                  <button class="quiet-button" type="button" @click="validateVersion(item.id)">
                    运行校验
                  </button>
                  <button
                    v-if="item.status === 'DRAFT' && canManage"
                    type="button"
                    :disabled="saving || validation[item.id]?.valid !== true"
                    @click="publishVersion(item.id, item.revision)"
                  >
                    发布版本
                  </button>
                </div>
                <ul
                  v-if="validation[item.id] && !validation[item.id]!.valid"
                  class="validation-list"
                >
                  <li v-for="reason in validation[item.id]!.errors" :key="reason">{{ reason }}</li>
                </ul>
                <p v-else-if="validation[item.id]?.valid" class="validation-ok">校验通过</p>
              </article>
            </div>
            <div v-else class="compact-empty">当前流程还没有版本</div>
          </section>

          <form
            v-if="selectedFlow && canManage"
            class="form-section flow-version-form"
            @submit.prevent="createVersion"
          >
            <div class="section-heading">
              <div>
                <p class="eyebrow">新建草稿</p>
                <h2>匹配条件与审批节点</h2>
              </div>
            </div>
            <div class="flow-meta-grid">
              <label
                ><span>生效时间</span
                ><input v-model="version.effectiveFrom" type="datetime-local" required
              /></label>
              <label
                ><span>失效时间（选填）</span
                ><input v-model="version.effectiveUntil" type="datetime-local"
              /></label>
              <label
                ><span>优先级</span><input v-model.number="version.priority" type="number" required
              /></label>
            </div>

            <div class="subsection-heading">
              <h3>匹配条件</h3>
              <button class="text-button" type="button" @click="addCondition">+ 添加条件</button>
            </div>
            <article
              v-for="(condition, index) in version.conditions"
              :key="index"
              class="config-row-card"
            >
              <div class="config-row-title">
                <strong>条件 {{ index + 1 }}</strong
                ><button
                  class="text-button danger-text"
                  type="button"
                  :disabled="version.conditions.length === 1"
                  @click="removeCondition(index)"
                >
                  移除
                </button>
              </div>
              <div class="config-grid">
                <label
                  ><span>组织</span
                  ><select v-model="condition.organizationId" required>
                    <option value="" disabled>请选择</option>
                    <option v-for="org in operatingOrganizations" :key="org.id" :value="org.id">
                      {{ org.code }} · {{ org.name }}
                    </option>
                  </select></label
                >
                <label
                  ><span>单据类型</span
                  ><select v-model="condition.documentType">
                    <option v-for="item in documentTypes" :key="item.value" :value="item.value">
                      {{ item.label }}
                    </option>
                  </select></label
                >
                <label
                  ><span>费用类别（选填）</span
                  ><select v-model="condition.expenseCategoryId">
                    <option value="">全部类别</option>
                    <option v-for="item in categories" :key="item.id" :value="item.id">
                      {{ item.code }} · {{ item.name }}
                    </option>
                  </select></label
                >
                <label
                  ><span>最低金额（含）</span
                  ><input v-model="condition.minAmount" inputmode="decimal" required
                /></label>
                <label
                  ><span>最高金额（不含）</span
                  ><input v-model="condition.maxAmount" inputmode="decimal" placeholder="不设上限"
                /></label>
                <label class="check-field"
                  ><input v-model="condition.includeDescendants" type="checkbox" /><span
                    >包含下级组织</span
                  ></label
                >
              </div>
            </article>

            <div class="subsection-heading">
              <h3>审批节点</h3>
              <button class="text-button" type="button" @click="addNode">+ 添加节点</button>
            </div>
            <article v-for="(node, index) in version.nodes" :key="index" class="config-row-card">
              <div class="config-row-title">
                <strong>节点 {{ index + 1 }}</strong
                ><button
                  class="text-button danger-text"
                  type="button"
                  :disabled="version.nodes.length === 1"
                  @click="removeNode(index)"
                >
                  移除
                </button>
              </div>
              <div class="config-grid">
                <label
                  ><span>节点名称</span><input v-model="node.name" maxlength="120" required
                /></label>
                <label
                  ><span>处理人类型</span
                  ><select v-model="node.nodeType">
                    <option v-for="item in nodeTypes" :key="item.value" :value="item.value">
                      {{ item.label }}
                    </option>
                  </select></label
                >
                <label
                  ><span>通过方式</span
                  ><select v-model="node.approvalMode">
                    <option :value="APPROVAL_MODE.ANY_ONE">任一人通过</option>
                    <option :value="APPROVAL_MODE.ALL">全部人通过</option>
                  </select></label
                >
                <label v-if="node.nodeType === APPROVAL_NODE_TYPE.ORG_ROLE"
                  ><span>候选角色</span
                  ><select v-model="node.candidateRoleCode" required>
                    <option value="" disabled>请选择</option>
                    <option v-for="item in roleOptions" :key="item.value" :value="item.value">
                      {{ item.label }}
                    </option>
                  </select></label
                >
                <label v-if="node.nodeType === APPROVAL_NODE_TYPE.FIXED_USER"
                  ><span>固定人员</span
                  ><select v-model="node.fixedUserId" required>
                    <option value="" disabled>请选择</option>
                    <option v-for="user in users" :key="user.id" :value="user.id">
                      {{ user.name }} ·
                      {{ user.passwordCredential?.username || user.employeeNo || '无账号名' }}
                    </option>
                  </select></label
                >
                <label v-if="node.nodeType !== APPROVAL_NODE_TYPE.APPLICANT_MANAGER"
                  ><span>限定组织（选填）</span
                  ><select v-model="node.scopeOrganizationId">
                    <option value="">按条件组织解析</option>
                    <option v-for="org in operatingOrganizations" :key="org.id" :value="org.id">
                      {{ org.code }} · {{ org.name }}
                    </option>
                  </select></label
                >
                <label
                  ><span>期望时长（小时）</span
                  ><input v-model.number="node.expectedDurationHours" type="number" min="1"
                /></label>
              </div>
            </article>
            <div class="sticky-form-action">
              <p>保存后形成不可直接编辑的草稿版本；修改请创建下一版本。</p>
              <button type="submit" :disabled="saving">
                {{ saving ? '正在保存…' : '创建草稿版本' }}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section class="form-section simulator-panel">
        <div class="section-heading">
          <div>
            <p class="eyebrow">只读预演</p>
            <h2>流程模拟器</h2>
            <p>模拟只解释当前已发布规则，不创建审批实例。</p>
          </div>
        </div>
        <form class="flow-inline-form" @submit.prevent="simulate">
          <label
            ><span>经营组织</span
            ><select v-model="simulator.operatingOrgId" required>
              <option value="" disabled>请选择</option>
              <option
                v-for="org in operatingOrganizations.filter((item) => item.type !== 'GROUP')"
                :key="org.id"
                :value="org.id"
              >
                {{ org.code }} · {{ org.name }}
              </option>
            </select></label
          >
          <label
            ><span>单据类型</span
            ><select v-model="simulator.documentType">
              <option v-for="item in documentTypes" :key="item.value" :value="item.value">
                {{ item.label }}
              </option>
            </select></label
          >
          <label
            ><span>费用类别（选填）</span
            ><select v-model="simulator.expenseCategoryId">
              <option value="">无/全部</option>
              <option v-for="item in categories" :key="item.id" :value="item.id">
                {{ item.name }}
              </option>
            </select></label
          >
          <label
            ><span>金额</span><input v-model="simulator.amount" inputmode="decimal" required
          /></label>
          <label
            ><span>模拟时间（选填）</span><input v-model="simulator.at" type="datetime-local"
          /></label>
          <button type="submit">运行模拟</button>
        </form>
        <article v-if="simulation" class="simulation-result">
          <div>
            <span class="status-pill" data-status="PUBLISHED">唯一命中</span
            ><strong
              >{{ simulation.flowCode }} · {{ simulation.flowName }} / 版本
              {{ simulation.versionNumber }}</strong
            >
          </div>
          <ol>
            <li v-for="node in simulation.nodes" :key="node.sequence">
              {{ node.sequence }}. {{ node.name }}（{{
                node.approvalMode === 'ALL' ? '全部通过' : '任一通过'
              }}）
            </li>
          </ol>
        </article>
      </section>
    </template>
  </main>
</template>
